import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Package, FileText } from "lucide-react";
import { EASE, Reveal } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import EnquiryModal from "../components/EnquiryModal";
import { CATEGORY_META } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setStatus("loading");
    window.scrollTo(0, 0);
    fetch(`${API}/products/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((p) => {
        if (!mounted) return;
        setProduct(p);
        setStatus("ok");
        return fetch(`${API}/products?category=${p.category}`)
          .then((r) => r.json())
          .then((list) => {
            if (mounted) setRelated((Array.isArray(list) ? list : []).filter((x) => x.slug !== slug).slice(0, 3));
          });
      })
      .catch(() => mounted && setStatus("missing"));
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="mx-auto max-w-7xl px-4 pt-[160px] pb-32 sm:px-6 lg:px-8">
        <div className="h-[480px] animate-pulse rounded-3xl bg-slate-200/70" />
      </div>
    );
  }

  if (status === "missing" || !product) {
    return (
      <div className="mx-auto max-w-7xl px-4 pt-[160px] pb-32 text-center sm:px-6 lg:px-8" data-testid="product-not-found">
        <p className="font-display text-3xl font-semibold text-ink">Product not found</p>
        <Link
          to="/products"
          data-testid="product-back-to-catalog"
          className="mt-6 inline-flex rounded-full bg-forest px-6 py-3 text-sm font-semibold text-white hover:bg-forest-hover"
        >
          Back to Catalog
        </Link>
      </div>
    );
  }

  const categoryName = CATEGORY_META[product.category]?.name || product.category;

  return (
    <div data-testid="page-product-detail" className="bg-mist">
      <section className="relative overflow-hidden pt-[72px]">
        <div className="dot-grid pointer-events-none absolute inset-0" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-12 sm:px-6 lg:px-8 lg:pb-28 lg:pt-16">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link
              to="/products"
              data-testid="product-breadcrumb-back"
              className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-forest"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Catalog
            </Link>
          </motion.div>

          <div className="mt-8 grid gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: EASE }}
            >
              <div className="relative overflow-hidden rounded-t-[8rem] rounded-b-[2rem] border border-slate-200 shadow-[0_40px_80px_-40px_rgba(6,78,59,0.4)]">
                <img
                  src={product.image}
                  alt={product.name}
                  className="img-editorial h-[420px] w-full object-cover sm:h-[560px]"
                />
                {product.badge && (
                  <span className="absolute left-5 top-5 rounded-full bg-white/90 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-forest backdrop-blur">
                    {product.badge}
                  </span>
                )}
              </div>
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
              data-testid="product-detail-info"
            >
              <p className="flex flex-wrap items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
                <Link to={`/products?category=${product.category}`} className="hover:text-forest">
                  {categoryName}
                </Link>
                <span className="h-1 w-1 rounded-full bg-gold" />
                <span className="text-slate-400">HS {product.hs_code}</span>
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                {product.name}
              </h1>
              <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                {product.tagline}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <span className="rounded-full bg-sage px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-forest" data-testid="product-moq-chip">
                  MOQ · {product.moq}
                </span>
                <span className="rounded-full border border-slate-200 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500" data-testid="product-hs-chip">
                  HS Code · {product.hs_code}
                </span>
              </div>

              <p className="mt-6 text-sm leading-relaxed text-slate-600 sm:text-base">
                {product.description}
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  data-testid="product-detail-quote-button"
                  onClick={() => setQuoteOpen(true)}
                  className="group flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-forest-hover hover:shadow-[0_16px_40px_-12px_rgba(6,78,59,0.55)]"
                >
                  Request Quotation
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <Link
                  to="/contact"
                  data-testid="product-detail-sample-button"
                  className="rounded-full border border-forest/25 px-7 py-3.5 text-sm font-semibold text-forest transition-colors hover:bg-forest hover:text-white"
                >
                  Ask for a Sample
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Specs + packaging */}
          <div className="mt-20 grid gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <div className="rounded-3xl border border-slate-200 bg-white p-7 sm:p-9" data-testid="product-specs-table">
                <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                  <FileText className="h-4 w-4" />
                  Technical Specifications
                </p>
                <dl className="mt-6">
                  {product.specs.map((spec, i) => (
                    <div
                      key={spec.label}
                      className={`flex flex-col gap-1 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8 ${
                        i < product.specs.length - 1 ? "border-b border-slate-100" : ""
                      }`}
                    >
                      <dt className="text-sm font-medium text-slate-500">{spec.label}</dt>
                      <dd className="text-sm font-semibold text-ink sm:text-right">{spec.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="h-full rounded-3xl bg-forest p-7 text-white sm:p-9" data-testid="product-packaging-list">
                <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
                  <Package className="h-4 w-4" />
                  Packaging & Shipping
                </p>
                <ul className="mt-6 space-y-4">
                  {product.packaging.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-white/90">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10">
                        <Check className="h-3 w-3" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-8 border-t border-white/15 pt-5 text-xs leading-relaxed text-white/60">
                  Load ports: Nhava Sheva (JNPT), Mundra &amp; Chennai. Incoterms: FOB, CIF, DDP.
                  Third-party inspection welcome before stuffing.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-24">
              <Reveal>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
                  More from {categoryName}
                </h2>
              </Reveal>
              <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((p, i) => (
                  <ProductCard key={p.slug} product={p} index={i} onRequestQuote={() => setQuoteOpen(true)} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <EnquiryModal open={quoteOpen} onClose={() => setQuoteOpen(false)} product={product} products={[product]} />
    </div>
  );
}
